import { ContentLink } from "@robe/client/xcore-ui";
import { Box, Card, ExtendedIconProps, Tag } from "@xcorejs/ui";
import { useLayout } from "xcore";
import { Innovation, InnovationValues } from "xcore/types";
import { InnovationPopover } from "./InnovationPopover";
import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@mui/joy";

type InnovationBoxInnovationValues = Pick<InnovationValues, "icon"> & Partial<InnovationValues>;

export type InnovationBoxInnovation = Innovation<keyof InnovationBoxInnovationValues>;

export type InnovationBoxProps = {
  innovation: InnovationBoxInnovation;
  openAs: "modal" | "link";
  popover?: boolean;
  backto?: number;
  expandUp?: boolean;
} & Omit<ExtendedIconProps, "popover">;

// icon, shortcut, intro, name, image fields are needed if innovation box is used as a popover and link
const InnovationBox = ({ innovation, openAs, popover, backto, expandUp, ...props }: InnovationBoxProps) => {
  const { image, stringify } = useLayout();
  const innovationIconUrl = innovation.values?.icon ? image(innovation.values.icon) : null;
  const [showLoading, setShowLoading] = useState<boolean>(true);

  const imgRef = useRef<HTMLImageElement | null>(null);

  // Check on mount if the image is already loaded from cache
  useEffect(() => {
    if (imgRef.current?.complete) {
      setShowLoading(false);
    }
  }, []);

  const renderBox = () => (
    <>
      <Card
        v="outline"
        cursor="pointer"
        padding="0"
        transition="box-shadow 300ms"
        border="2px solid transparent"
        borderRadius="2px"
        boxShadow="0 0.5rem 1rem 0 rgba(30, 30, 30, 0.2)"
        _body={{
          padding: 0,
        }}
        _hover={{
          boxShadow: "0 .5rem 1rem 0 rgba(224, 0, 52, 0.5)",
          borderRadius: "2px",
        }}
        {...props}
      >
        {innovationIconUrl ? (
          <Box width="6.4rem" height="6.4rem">
            {showLoading && <Skeleton variant="rectangular" width="6.4rem" height="6.4rem" />}
            <img
              ref={imgRef}
              src={innovationIconUrl}
              onLoad={() => setShowLoading(false)}
              style={{ display: showLoading ? "none" : "block" }}
              width="100%"
              height="100%"
            />
          </Box>
        ) : (
          <Tag
            width="6.4rem"
            height="6.4rem"
            alignItems="center"
            justifyContent="center"
            bg="white"
            borderColor="transparent"
            color="black"
          >
            {innovation.values?.shortcut}
          </Tag>
        )}
      </Card>
    </>
  );

  return popover ? (
    <InnovationPopover
      openAs={openAs}
      expandUp={expandUp}
      intro={stringify(innovation.values?.intro)}
      name={stringify(innovation.values?.name)}
      innovation={innovation}
      backto={backto}
    >
      {renderBox()}
    </InnovationPopover>
  ) : openAs === "link" ? (
    <ContentLink content={innovation} {...(backto && { queries: { backto } })}>
      {renderBox()}
    </ContentLink>
  ) : (
    renderBox()
  );
};

export default InnovationBox;
